import _SayItBackMutation from "../__generated__/SayItBackMutation.graphql";
import { useCallback, useEffect, useRef, useState } from 'react';
import { graphql, useMutation } from 'react-relay';
import type { Disposable } from 'relay-runtime';
import { Loader2, Mic } from 'lucide-react';
import { AudioBridge } from '../audio/bridge';
import { t, type Message, type PlainKey } from '../i18n/t';
import { En } from '../i18n/En';
import { keyGesture } from '../audio/gesture.ts';
import { codeFromError } from './aidErrors.ts';
import { AttemptRecorder, LABELS, cardView, compare, copyForAttempt, micErrorCopy, pcmBase64, type RecorderEvent, type ViewPhase, type Word } from './sayItBack.ts';
import type { SayItBackMutation } from '../__generated__/SayItBackMutation.graphql';
const SayItBackAttempt = (_SayItBackMutation.hash && _SayItBackMutation.hash !== "35595c960aabf90e8989d45473398ce2" && console.error("The definition of 'SayItBackMutation' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _SayItBackMutation);
type Phase = ViewPhase;

// `AttemptRecorder` refuses a press until `settle()`, so nothing should have moved
// the phase on; this only keeps an answer from ever relabelling a live hold.
const settled = (p: Phase): Phase => p === 'sending' ? 'ready' : p;

/**
 * Hold, say the correction, see it beside the target (say-it-back spec §4).
 * A mirror, never a mark: no verdict, no stored attempt, no outcome (§2).
 * Rendered only inside `HubDetail`'s exemplar block, which `exemplarFor`
 * already withholds from a child (§3).
 */
export function SayItBack({
  practiceItemId,
  target
}: {
  practiceItemId: string;
  target: string;
}) {
  const [commit] = useMutation<SayItBackMutation>(SayItBackAttempt);
  const [phase, setPhase] = useState<Phase>('off');
  const [heard, setHeard] = useState<string | null>(null);
  const [fresh, setFresh] = useState(false);
  // Remounts the result on each answer, so the live region announces a
  // repeat of the same words rather than seeing no change.
  const [answer, setAnswer] = useState(0);
  const [error, setError] = useState<Message | null>(null);
  const recorder = useRef<AttemptRecorder | null>(null);
  const inFlight = useRef<Disposable | null>(null);
  const onEvent = useCallback((e: RecorderEvent) => {
    switch (e.kind) {
      case 'starting':
        setError(null);
        setPhase('starting');
        break;
      case 'ready':
        setPhase('ready');
        break;
      case 'recording':
        // §4.7: a new attempt replaces the last one, so a tooShort or an
        // error alert never sits under the previous attempt's words. The
        // words are held, not dropped: see `cardView`.
        setError(null);
        setFresh(false);
        setPhase('recording');
        break;
      case 'sending':
        setPhase('sending');
        break;
      case 'tooShort':
        setPhase('ready');
        setError(copyForAttempt('BAD_USER_INPUT'));
        break;
      case 'micError':
        setPhase('off');
        setError(micErrorCopy(e.error));
        break;
      case 'clip':
        inFlight.current?.dispose();
        inFlight.current = commit({
          variables: {
            input: {
              practiceItemId,
              pcmBase64: pcmBase64(e.pcm)
            }
          },
          onCompleted: ({
            sayItBack
          }) => {
            inFlight.current = null;
            recorder.current?.settle();
            setPhase(settled);
            setHeard(sayItBack.transcript);
            setFresh(true);
            setAnswer(n => n + 1);
          },
          onError: err => {
            inFlight.current = null;
            recorder.current?.settle();
            setPhase(settled);
            setError(copyForAttempt(codeFromError(err)));
          }
        });
        break;
    }
  }, [commit, practiceItemId]);

  // One microphone per attempt (media-channel design §4.5): `AttemptRecorder`
  // releases it before the answer, so "Hear it" beside this card never plays
  // on the call channel. Disposed on unmount with any request in flight, so
  // no late answer lands on an unmounted component (§4.7).
  useEffect(() => {
    const r = new AttemptRecorder((onPcm, onEnded) => new AudioBridge(onPcm, onEnded, () => {}), onEvent);
    recorder.current = r;
    return () => {
      r.dispose();
      inFlight.current?.dispose();
      inFlight.current = null;
      recorder.current = null;
    };
  }, [onEvent]);
  const down = useCallback((e: React.PointerEvent) => {
    // Pointer capture, or a release outside the button never arrives (as
    // `SessionView`'s mic control argues).
    e.currentTarget.setPointerCapture(e.pointerId);
    recorder.current?.press();
  }, []);
  const up = useCallback(() => recorder.current?.release(), []);
  const onKey = useCallback((kind: 'down' | 'up') => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') e.preventDefault();
    const g = keyGesture(e.key, kind, e.repeat);
    if (g === 'down') recorder.current?.press();else if (g === 'up') recorder.current?.release();
  }, []);
  const view = cardView(phase, {
    heard,
    fresh,
    error
  });
  const {
    busy
  } = view;
  const result = heard === null ? null : compare(target, heard);
  return <div className="mt-2 flex flex-col">
      <button type="button"
    // `aria-disabled`, not `disabled`: an actually-disabled button drops
    // keyboard focus to the body (HTML focus fixup), and the next Space
    // then scrolls the page instead of releasing the hold -- spec §4.1
    // requires Enter/Space to keep working. A press while busy starts
    // nothing: `AttemptRecorder` ignores it until the answer `settle()`s the
    // clip, and a press still held when the microphone finishes opening
    // is the learner's first attempt, not a busy click. daisyUI's button
    // styles `[aria-disabled=true]` the same as `:disabled`, so the look
    // survives too.
    //
    // `touch-none`: without it a thumb that drifts ~10 px mid-hold is
    // taken as a pan, the browser cancels the pointer (ending the attempt
    // as too short) and scrolls the card under it -- measured, BUILD.md
    // "Say it back holds still". The iOS long-press callout goes with it.
    className={`btn btn-sm min-h-(--hit-min) gap-2 self-start touch-none select-none [-webkit-touch-callout:none] ${phase === 'recording' ? 'btn-error' : 'btn-outline'}`} aria-pressed={phase === 'recording'} aria-disabled={busy} onPointerDown={down} onPointerUp={up} onPointerCancel={up} onKeyDown={onKey('down')} onKeyUp={onKey('up')}>
        {busy ? <Loader2 aria-hidden className="size-4 animate-spin" /> : <Mic aria-hidden className="size-4" />}
        {/* Every label in one grid cell, the button as wide as the widest;
            the hidden ones drop out of the accessible name. */}
        <span className="grid text-start">
          {[...new Set<PlainKey>(Object.values(LABELS))].map(l => <span key={l} aria-hidden={l !== view.label} className={`col-start-1 row-start-1 ${l === view.label ? '' : 'invisible'}`}>
              {t(l)}
            </span>)}
        </span>
      </button>
      {/* One line reserved for the hint or an alert, so neither arriving nor
          going moves what is below. */}
      <div className="mt-2 min-h-4 text-xs">
        {view.status && (view.status.alert ? <p role="alert" className="text-error">
              {t(view.status.text)}
            </p> : <p className="text-base-content/50">{t(view.status.text)}</p>)}
      </div>
      <div aria-live="polite" className={result ? 'mt-2' : undefined}>
        {result && <div key={answer} aria-hidden={view.result === 'held'} className={`flex flex-col gap-2 ${view.result === 'held' ? 'invisible' : ''}`}>
            {result.said.length === 0 ? <p className="text-sm">{t('say-back-heard-nothing')}</p> : <>
                <Line label={t('say-back-target')} words={result.target} />
                <Line label={t('say-back-said')} words={result.said} />
                {result.same && <p className="text-sm">{t('say-back-same')}</p>}
                <p className="text-base-content/50 text-xs">{t('say-back-caveat')}</p>
              </>}
          </div>}
      </div>
    </div>;
}

/** A line with its differing words underlined: marked, never struck or coloured as wrong. */
function Line({
  label,
  words
}: {
  label: string;
  words: Word[];
}) {
  return <div>
      <p className="text-base-content/50 text-xs">{label}</p>
      <p>
        <En>
          {words.map((w, i) => <span key={i}>
              {i > 0 && ' '}
              {w.differs ? <u className="decoration-warning decoration-2 underline-offset-4">{w.text}</u> : w.text}
            </span>)}
        </En>
      </p>
    </div>;
}